import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [drawer, setDrawer] = useState(null); // null | {} (new) | coupon (edit)

  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data.items || []));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (c) => {
    await api.patch(`/admin/coupons/${c.code}`, { is_active: !c.is_active });
    toast.success(`${c.code} ${!c.is_active ? "activated" : "deactivated"}`);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.code}`);
    toast.success(`${c.code} deleted`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-coupons">
      <div className="flex items-end justify-between gap-3">
        <div>
          <span className="label-overline text-jow-gold">Promos</span>
          <h1 className="font-display text-3xl text-jow-navy mt-1">Coupons</h1>
          <p className="text-xs text-jow-navy/55 mt-1">{items.length} active codes</p>
        </div>
        <button
          onClick={() => setDrawer({})}
          className="btn-primary-jow text-[11px]"
          data-testid="new-coupon-btn"
        >
          <Plus size={13} /> New coupon
        </button>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Code</th>
            <th className="text-left px-4 py-3">Type</th>
            <th className="text-right px-4 py-3">Value</th>
            <th className="text-right px-4 py-3">Min cart</th>
            <th className="text-right px-4 py-3">Used / limit</th>
            <th className="text-center px-4 py-3">Active</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.code} className="border-t border-jow-navy/5" data-testid={`coupon-${c.code}`}>
              <td className="px-4 py-3 font-display text-jow-navy">{c.code}</td>
              <td className="px-4 py-3 text-jow-navy/70">{c.type}</td>
              <td className="px-4 py-3 text-right">
                {c.type === "percent" ? `${c.value}%` : `₹${c.value}`}
              </td>
              <td className="px-4 py-3 text-right">₹{(c.min_cart || 0).toLocaleString("en-IN")}</td>
              <td className="px-4 py-3 text-right text-jow-navy/70">
                {c.used_count || 0} / {c.usage_limit}
              </td>
              <td className="px-4 py-3 text-center">
                <button
                  onClick={() => toggle(c)}
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    c.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  }`}
                  data-testid={`coupon-toggle-${c.code}`}
                >
                  {c.is_active ? "on" : "off"}
                </button>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => setDrawer(c)}
                  className="text-jow-pink mr-2"
                  data-testid={`coupon-edit-${c.code}`}
                >
                  <Edit size={13} />
                </button>
                <button
                  onClick={() => remove(c)}
                  className="text-jow-navy/40"
                  data-testid={`coupon-del-${c.code}`}
                >
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {drawer && (
        <Drawer
          coupon={drawer.code ? drawer : null}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Drawer({ coupon, onClose, onSaved }) {
  const [form, setForm] = useState(
    coupon || {
      code: "",
      type: "percent",
      value: 10,
      min_cart: 999,
      max_discount: null,
      expires_at: "",
      usage_limit: 1000,
      is_active: true,
      description: "",
    }
  );

  const save = async () => {
    try {
      if (coupon) {
        const body = { ...form };
        delete body.code;
        await api.patch(`/admin/coupons/${coupon.code}`, body);
        toast.success("Coupon updated.");
      } else {
        await api.post("/admin/coupons", form);
        toast.success("Coupon created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
      onClick={onClose}
      data-testid="coupon-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-jow-navy">
            {coupon ? `Edit ${coupon.code}` : "New coupon"}
          </h2>
          <button onClick={onClose} className="text-jow-navy/50">
            <X size={16} />
          </button>
        </div>
        {!coupon && (
          <Input label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="c-code" />
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-1">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
              data-testid="c-type"
            >
              <option value="percent">Percent</option>
              <option value="flat">Flat</option>
            </select>
          </div>
          <Input
            label="Value"
            type="number"
            value={form.value}
            onChange={(v) => setForm({ ...form, value: Number(v) })}
            testid="c-value"
          />
          <Input
            label="Min cart"
            type="number"
            value={form.min_cart}
            onChange={(v) => setForm({ ...form, min_cart: Number(v) })}
            testid="c-min-cart"
          />
          <Input
            label="Max discount"
            type="number"
            value={form.max_discount || ""}
            onChange={(v) => setForm({ ...form, max_discount: v ? Number(v) : null })}
            testid="c-max-disc"
          />
          <Input
            label="Usage limit"
            type="number"
            value={form.usage_limit}
            onChange={(v) => setForm({ ...form, usage_limit: Number(v) })}
            testid="c-limit"
          />
          <Input
            label="Expires at"
            value={form.expires_at || ""}
            onChange={(v) => setForm({ ...form, expires_at: v })}
            testid="c-exp"
          />
        </div>
        <Input
          label="Description"
          value={form.description || ""}
          onChange={(v) => setForm({ ...form, description: v })}
          testid="c-desc"
        />
        <label className="text-sm flex items-center gap-2 text-jow-navy/70">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            data-testid="c-active"
          />
          Active
        </label>
        <button onClick={save} className="btn-primary-jow w-full" data-testid="c-save">
          {coupon ? "Save" : "Create"}
        </button>
      </aside>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}
